import React, { useState } from 'react';
import { useSEO } from '../../../hooks/useSEO';
import { WD_CONTENT as C } from './content';
import './WebinarDreLanding.scss';

type Status = 'idle' | 'loading' | 'success' | 'error';

const WebinarDreLanding: React.FC = () => {
  useSEO({
    title: C.seo.title,
    description: C.seo.description,
    canonical: '/webinar-dre',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: 'Webinar de DRE — Sua empresa deu lucro mês passado?',
      description: C.seo.description,
      eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
      eventStatus: 'https://schema.org/EventScheduled',
      startDate: '2026-07-16T20:00:00-03:00',
      url: 'https://77gestao.com.br/webinar-dre',
      organizer: { '@type': 'Organization', name: '77Gestão', url: 'https://77gestao.com.br' },
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'BRL',
        availability: 'https://schema.org/InStock',
        url: 'https://77gestao.com.br/webinar-dre',
      },
    },
  });

  const [status, setStatus] = useState<Status>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }
    setStatus('loading');
    try {
      const res = await fetch(C.form.endpoint, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form), // multipart/form-data; o navegador define o Content-Type
      });
      if (res.ok) {
        setStatus('success');
      } else {
        throw new Error('HTTP ' + res.status);
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="webinar-dre-landing">
      {/* ── Hero + formulário ─────────────────────────────────────────── */}
      <section className="wd-hero" id="inscricao" aria-labelledby="wd-hero-title">
        <div className="wd-container wd-hero__grid">
          <div className="wd-hero__text">
            <span className="wd-badge"><span className="wd-badge__dot" />{C.hero.badge}</span>
            <h1 id="wd-hero-title">{C.hero.title}</h1>
            <p className="wd-sub">{C.hero.subtitle}</p>
            <div className="wd-meta">
              <span><i className="fa-regular fa-calendar" aria-hidden="true" /> {C.event.dateLabel}</span>
              <span><i className="fa-regular fa-clock" aria-hidden="true" /> {C.event.timeLabel}</span>
              <span><i className="fa-solid fa-video" aria-hidden="true" /> {C.event.formatLabel}</span>
            </div>
          </div>

          {/* Card do formulário */}
          <div className="wd-form-card">
            {status === 'success' ? (
              <div className="wd-success">
                <div className="wd-success__check"><i className="fa-solid fa-circle-check" aria-hidden="true" /></div>
                <h2>{C.form.success.title}</h2>
                <p>{C.form.success.text}</p>
              </div>
            ) : (
              <>
                <h2>{C.form.title}</h2>
                <span className="wd-form-card__free">{C.form.free}</span>

                <form onSubmit={handleSubmit} noValidate>
                  {/* Static Forms */}
                  <input type="hidden" name="apiKey" value={C.form.apiKey} />
                  <input type="hidden" name="subject" value={C.form.subject} />
                  {/* Honeypot anti-spam */}
                  <input
                    type="text"
                    name="honeypot"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="wd-honeypot"
                  />

                  <label htmlFor="wd-nome">Nome completo</label>
                  <input type="text" id="wd-nome" name="name" required placeholder="Seu nome" />

                  <label htmlFor="wd-email">E-mail</label>
                  <input type="email" id="wd-email" name="email" required placeholder="voce@empresa.com.br" />

                  <label htmlFor="wd-whatsapp">WhatsApp (para o lembrete)</label>
                  <input type="tel" id="wd-whatsapp" name="whatsapp" required placeholder="(00) 00000-0000" />

                  <label htmlFor="wd-controle">{C.form.controleLabel}</label>
                  <select id="wd-controle" name="como_controla_financas" required defaultValue="">
                    <option value="" disabled>Selecione...</option>
                    {C.form.controleOptions.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>

                  <button type="submit" disabled={status === 'loading'}>
                    {status === 'loading' ? C.form.submitting : C.form.submit}
                  </button>

                  {status === 'error' && <p className="wd-form-erro">{C.form.errorMsg}</p>}
                  <p className="wd-privacy">{C.form.privacy}</p>
                </form>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ── 3 caminhos ────────────────────────────────────────────────── */}
      <section className="wd-section" aria-labelledby="wd-caminhos-title">
        <div className="wd-container">
          <h2 className="wd-title" id="wd-caminhos-title">{C.caminhos.title}</h2>
          <p className="wd-lead">{C.caminhos.lead}</p>
          <div className="wd-cards">
            {C.caminhos.items.map((item) => (
              <article className="wd-card" key={item.num}>
                <div className="wd-card__icon"><i className={item.icon} aria-hidden="true" /></div>
                <div className="wd-card__num">{item.num}</div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Para quem é ───────────────────────────────────────────────── */}
      <section className="wd-section wd-section--alt" aria-labelledby="wd-paraquem-title">
        <div className="wd-container">
          <h2 className="wd-title" id="wd-paraquem-title">{C.paraQuem.title}</h2>
          <ul className="wd-bullets">
            {C.paraQuem.items.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── Quem conduz ───────────────────────────────────────────────── */}
      <section className="wd-section" aria-labelledby="wd-host-title">
        <div className="wd-container">
          <h2 className="wd-title" id="wd-host-title">{C.host.title}</h2>
          <p className="wd-lead">{C.host.text}</p>
        </div>
      </section>

      {/* ── CTA final ─────────────────────────────────────────────────── */}
      <section className="wd-cta-final" aria-labelledby="wd-cta-title">
        <div className="wd-container">
          <h2 id="wd-cta-title">{C.ctaFinal.title}</h2>
          <p>{C.ctaFinal.subtitle}</p>
          <a className="wd-cta-final__btn" href="#inscricao">{C.ctaFinal.cta}</a>
        </div>
      </section>
    </div>
  );
};

export default WebinarDreLanding;
